import { useQuery } from '@tanstack/react-query';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase/client';
import { Loader } from '@mantine/core';
import { useNavigate } from 'react-router-dom';

async function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default function FreeTestPage() {
  const { session } = useAuth();

  const { data:user } = useQuery({
    queryKey: ['FreeTestUser'],
    queryFn: async () => {
      if (session?.user) {
        return session.user;
      }

      const { data, error } = await supabase.auth.signInAnonymously();
      if (error) {
        console.log(error);
        return null;
      }

      return data.user;
    }
  });

  const { data:testId } = useQuery({
    queryKey: ['FreeTest'],
    queryFn: async () => {
      await delay(20_000);
      const { data, error } = await supabase.rpc('free_test', {
        cat: "lsat"
      }).single();

      if (error) {
        console.log(error);
      }

      return data;
    },
    enabled: !!user,
  })
  
  return <LoadingPage testId={testId} />;
}

const LoadingPage = ({ testId }: {testId?: string | null}) => {
  const navigate = useNavigate();
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Collecting test materials...');
  const [startingTest, setStartingTest] = useState(false);

  // Array of loading messages to display sequentially
  const loadingMessages = [
    'Collecting test materials...',
    'Analyzing question patterns...',
    'Calibrating difficulty levels...',
    'Organizing logical reasoning sections...',
    'Finalizing reading comprehension passages...',
    'Preparing analytical reasoning games...',
    'Almost ready...'
  ];
  
  useEffect(() => {
    // Simulate loading progress
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          (messageInterval && clearInterval(messageInterval));
          setLoadingMessage('Your test is now ready!');
          clearInterval(interval);
          return 100;
        }
        return prev + 1;
      });
    }, 200);
    
    // Change loading message every few seconds
    const messageInterval = setInterval(() => {
      const currentIndex = loadingMessages.indexOf(loadingMessage);
      const nextIndex = (currentIndex + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[nextIndex]);
    }, 3000);
    
    return () => {
      clearInterval(interval);
      clearInterval(messageInterval);
    };
  }, [loadingMessage]);
  
  // Paper shuffling animation for background elements
  const paperVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.2,
        duration: 0.5,
        repeat: Infinity,
        repeatType: 'reverse',
        repeatDelay: 5 + index,
      }
    })
  };
 
  async function startTest() {
    if (!testId) {
      return;
    }

    setStartingTest(true);

    try {
      const { error } = await supabase.rpc('start_test', {
        _test_id: testId
      });

      if (error) {
        // TODO: error
        console.log(error);
        return;
      }

      navigate(`/test/${testId}`);
    } catch (e) {
      console.log(e);
    } finally {
      setStartingTest(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col items-center pt-10 px-4">
      <div className="max-w-2xl w-full relative">
        {/* Background animated papers */}
        <div className="absolute inset-0 -z-10">
          {[...Array(5)].map((_, index) => (
            <motion.div
              key={index}
              className="absolute bg-white rounded-lg shadow-md p-6"
              style={{
                width: `${250 - index * 10}px`,
                height: `${300 - index * 10}px`,
                top: `${index * 5}px`,
                left: `${index * 10}px`,
                zIndex: -index,
              }}
              custom={index}
              variants={paperVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="w-full h-2 bg-gray-200 rounded-full mb-2"></div>
              <div className="w-3/4 h-2 bg-gray-200 rounded-full mb-4"></div>
              {[...Array(6)].map((_, lineIndex) => (
                <div 
                  key={lineIndex} 
                  className="w-full h-2 bg-gray-200 rounded-full mb-2"
                  style={{ opacity: 0.7 - lineIndex * 0.1 }}
                ></div>
              ))}
            </motion.div>
          ))}
        </div>
        
        {/* Main content card */}
        <motion.div 
          className="bg-white rounded-xl shadow-xl p-8 relative z-10"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold text-indigo-800 mb-2">Preparing Your LSAT Practice Test</h2>
              <p className="text-gray-600">We're creating a personalized practice experience for you</p>
            </motion.div>
          </div>
          
          {/* Animated icons */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-8">
              {/* Book icon */}
              <motion.div
                className="flex flex-col items-center"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <div className="w-16 h-16 bg-indigo-100 rounded-md flex items-center justify-center mb-2 relative overflow-hidden">
                  <motion.div 
                    className="absolute w-12 h-2 bg-indigo-400 rounded"
                    animate={{ y: [0, 16, 0] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                    style={{ top: '14px' }}
                  />
                  <motion.div 
                    className="absolute w-10 h-2 bg-indigo-300 rounded"
                    animate={{ y: [0, 14, 0] }}
                    transition={{ repeat: Infinity, duration: 2, delay: 0.3, ease: "easeInOut" }}
                    style={{ top: '18px' }}
                  />
                  <motion.div 
                    className="absolute w-12 h-2 bg-indigo-400 rounded"
                    animate={{ y: [0, 12, 0] }}
                    transition={{ repeat: Infinity, duration: 2, delay: 0.6, ease: "easeInOut" }}
                    style={{ top: '22px' }}
                  />
                </div>
                <span className="text-sm text-gray-700">Questions</span>
              </motion.div>
              
              {/* Brain icon */}
              <motion.div
                className="flex flex-col items-center"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
              >
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mb-2 relative">
                  <motion.div 
                    className="absolute w-8 h-8 border-t-4 border-r-4 border-pink-400 rounded-full"
                    style={{ top: '3px', right: '3px' }}
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
                  />
                  <motion.div 
                    className="absolute w-6 h-6 border-b-4 border-l-4 border-pink-500 rounded-full"
                    style={{ bottom: '3px', left: '3px' }}
                    animate={{ rotate: -360 }}
                    transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
                  />
                </div>
                <span className="text-sm text-gray-700">Logic</span>
              </motion.div>
              
              {/* Clock icon */}
              <motion.div
                className="flex flex-col items-center"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2 relative">
                  <div className="w-2 h-2 bg-blue-500 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                  <motion.div 
                    className="w-1 h-6 bg-blue-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-full origin-bottom"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 6, ease: "linear" }}
                  />
                  <motion.div 
                    className="w-1 h-5 bg-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-full origin-bottom"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 30, ease: "linear" }}
                  />
                </div>
                <span className="text-sm text-gray-700">Timing</span>
              </motion.div>
            </div>
          </div>
          
          {/* Loading progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>{loadingMessage}</span>
              <span>{loadingProgress}%</span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"
                style={{ width: `${loadingProgress}%` }}
                initial={{ width: '0%' }}
                animate={{ width: `${loadingProgress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
          
          {/* Tips section */}
          <motion.div
            className="bg-indigo-50 rounded-lg p-4 border border-indigo-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.5 }}
          >
            <h3 className="text-sm font-semibold text-indigo-800 mb-2">Quick LSAT Tip</h3>
            <p className="text-sm text-gray-700">
              When tackling logical reasoning questions, identify the conclusion first, then examine the evidence provided to support it.
            </p>
          </motion.div>
          
          {
            loadingProgress >= 100 ?
              <motion.div 
                className="mt-8 flex justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <button 
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
                  onClick={startTest}
                >
                  <div className="flex items-center space-x-2">
                    {
                      startingTest ? <Loader type="dots" c="white" /> :
                      <>
                        <span>Start Your Test</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </>
                    }
                  </div>
                </button>
              </motion.div>
            : <div className="mt-8 relative h-4 overflow-hidden">
              <motion.div
                className="absolute left-0 h-1 bg-indigo-400 rounded"
                initial={{ width: 0 }}
                animate={{ 
                  width: ["0%", "100%", "0%"],
                  left: ["0%", "0%", "100%"]
                }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 4,
                  ease: "easeInOut"
                }}
              />
            </div>
          }
        </motion.div>
      </div>
    </div>
  );
};
